/**
 * Loom onboarding analytics.
 *
 * Central definitions for the GASv3 events fired by the Loom
 * onboarding flow. Keeping them here means:
 *  - the `action` / `actionSubject` / `source` triple is authored once,
 *  - the payload shape is type-checked at every call site,
 *  - and there is a single grep target for analytics ownership reviews.
 *
 * All events fire on the `marketing` channel, which is the WAC React path
 * (see `services/wac-web/src/analytics/wac-analytics/`).
 */

import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type {
	DefaultAnalyticsAttributes,
	DefaultStepAnalyticsAttributes,
	StepActions,
	UseCase,
} from './types';

const CHANNEL = 'marketing';
const SOURCE = 'loomOnboarding';

export const useLoomOnboardingAnalytics = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	/**
	 * Fired exactly once when the onboarding flow starts. Call from a
	 * `useEffect` guarded by a `hasStarted` ref.
	 */
	const fireOnboardingStarted = useCallback(
		(defaultAttributes: DefaultAnalyticsAttributes) => {
			createAnalyticsEvent({
				eventType: 'track',
				action: 'initiated',
				actionSubject: 'onboardingFlow',
				source: SOURCE,
				attributes: { ...defaultAttributes },
			}).fire(CHANNEL);
		},
		[createAnalyticsEvent],
	);

	/**
	 * Fired every time an onboarding step becomes visible to the user.
	 * Call from a `useEffect` keyed on the current step id.
	 */
	const fireStepViewed = useCallback(
		(defaultAttributes: DefaultStepAnalyticsAttributes, isFirstStep: boolean) => {
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'viewed',
				actionSubject: 'onboardingStep',
				source: SOURCE,
				attributes: { ...defaultAttributes, isFirstStep },
			}).fire(CHANNEL);
		},
		[createAnalyticsEvent],
	);

	/**
	 * Fired when the user completes a step (i.e. clicks Continue and the
	 * step manager accepts the transition).
	 */
	const fireStepCompleted = useCallback(
		(
			defaultAttributes: DefaultStepAnalyticsAttributes,
			action?: StepActions,
			persona?: UseCase,
		) => {
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'completed',
				actionSubject: 'onboardingStep',
				source: SOURCE,
				attributes: { ...defaultAttributes, action, persona },
			}).fire(CHANNEL);
		},
		[createAnalyticsEvent],
	);

	/**
	 * Fired exactly once when the user finishes the last step of the flow.
	 * `track` eventType — this is a business-milestone event, not a UI
	 * interaction.
	 * - Completion Time: Whole number of seconds it took for the user to complete the flow
	 */
	const fireFlowCompleted = useCallback(
		(hasProvisioningTimeLeft: boolean, defaultAttributes: DefaultAnalyticsAttributes) => {
			createAnalyticsEvent({
				eventType: 'track',
				action: 'completed',
				actionSubject: 'onboardingFlow',
				source: SOURCE,
				attributes: { ...defaultAttributes, hasProvisioningTimeLeft },
			}).fire(CHANNEL);
		},
		[createAnalyticsEvent],
	);

	return { fireOnboardingStarted, fireStepViewed, fireStepCompleted, fireFlowCompleted };
};
